import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import CloseIcon from '@mui/icons-material/Close'

import { useGetAvailableActsQuery } from '@/core/services/network'
import { applyPractitionerOverride, evaluateActConditions, ActCondition } from '@/core/utils/act-conditions.util'

// ─────────────────────────────────────────────────────────────────────────────
// Legacy types kept for backward compat with parent components
// ─────────────────────────────────────────────────────────────────────────────

/** @deprecated - kept for backward compatibility */
import { Act } from '@/core/models/consultation.model'
export type GenerateFSPFormValues = {
  Acts: Act[]
}

// ─────────────────────────────────────────────────────────────────────────────
// New props interface
// ─────────────────────────────────────────────────────────────────────────────

export type GenerateFSPDialogProps = {
  /** The consultation ID to fetch available acts for */
  consultationId?: string
  /** End date of the consultation — used to evaluate time/age conditions */
  consultationEndDate?: string | Date
  /** Patient date of birth — used to evaluate age conditions */
  patientDob?: string | Date
  isGenerating?: boolean
  onClose?: () => void
  /**
   * Called with the selected act IDs (new format) and a legacy Acts array
   * (backward compat) when the user confirms.
   */
  onGenerateFSP?: (actIds: string[], legacyActs: Act[]) => void
  open: boolean
  // Legacy props — kept so existing parents don't break at runtime
  initialValues?: GenerateFSPFormValues
}

interface AvailableAct {
  _id: string
  libelle: string
  id_transposition: string
  honoraire: string | number
  conditions?: ActCondition[]
  autoCreateFSP?: boolean
  establishmentId?: string
  role?: string
  /** Practitioner the `staff` conditions are evaluated against (set by the API per config role) */
  practitionerId?: string
}

export function GenerateFspDialog(props: GenerateFSPDialogProps) {
  const {
    consultationId,
    consultationEndDate,
    patientDob,
    initialValues,
    isGenerating = false,
    onClose,
    onGenerateFSP,
    open,
  } = props

  const { t } = useTranslation()

  // Legacy mode: caller (e.g. ExpertisePage) provides acts directly via
  // initialValues and has no consultationId. Skip the server fetch and render
  // the provided acts as selectable entries.
  const legacyActs = initialValues?.Acts ?? []
  const useLegacyMode = !consultationId && legacyActs.length > 0

  const {
    data: availableActsData,
    isLoading: isLoadingActs,
    isError,
  } = useGetAvailableActsQuery(consultationId ?? '', { skip: !consultationId || !open })

  const availableActs: AvailableAct[] = useMemo(() => {
    if (useLegacyMode) {
      return legacyActs.map((a, i) => ({
        _id: `legacy-${i}`,
        libelle: a.code,
        id_transposition: a.code,
        honoraire: a.price,
      }))
    }
    return (availableActsData?.acts ?? []).filter((a: AvailableAct) => a.autoCreateFSP === true)
  }, [useLegacyMode, legacyActs, availableActsData])

  // Evaluate which acts should be pre-checked based on their conditions
  const defaultChecked = useMemo(() => {
    if (useLegacyMode) {
      return new Set(availableActs.map((act) => act._id))
    }
    const endDate = consultationEndDate ? new Date(consultationEndDate) : new Date()
    const dob = patientDob ? new Date(patientDob) : undefined
    const firing = availableActs.filter((act) =>
      evaluateActConditions(act.conditions ?? [], { consultationEndDate: endDate, patientDob: dob, practitionerId: act.practitionerId })
    )
    // Practitioner-specific acts replace the default ones of the same establishment + role
    return new Set(applyPractitionerOverride(firing).map((act) => act._id))
  }, [useLegacyMode, availableActs, consultationEndDate, patientDob])

  // Track user overrides separately from defaults so the checkbox state is
  // correct on the very first render (before any effect fires). `null` means
  // "no user interaction yet — use the defaults".
  const [userCheckedIds, setUserCheckedIds] = useState<Set<string> | null>(null)
  const checkedIds = userCheckedIds ?? defaultChecked

  // Reset on dialog close so a fresh open recomputes from defaults
  useEffect(() => {
    if (!open) setUserCheckedIds(null)
  }, [open])

  const toggleAct = (id: string) => {
    setUserCheckedIds((prev) => {
      const next = new Set(prev ?? defaultChecked)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleConfirm = () => {
    if (useLegacyMode) {
      const selectedLegacyActs = legacyActs.filter((_, i) => checkedIds.has(`legacy-${i}`))
      onGenerateFSP?.([], selectedLegacyActs)
      return
    }
    const selected = availableActs.filter((a) => checkedIds.has(a._id))
    const actIds = selected.map((a) => a._id)
    const mappedLegacyActs: Act[] = selected.map((a) => ({
      date: new Date().toISOString(),
      code: a.id_transposition,
      price: parseFloat(String(a.honoraire)) || 0,
    }))
    onGenerateFSP?.(actIds, mappedLegacyActs)
  }

  const formatPrice = (honoraire: string | number) => {
    const n = parseFloat(String(honoraire))
    return isNaN(n) ? String(honoraire) : `${n.toFixed(2)} €`
  }

  return (
    <Dialog
      onClose={onClose}
      open={open}
      PaperProps={{ sx: { maxWidth: '640px', width: '100%' } }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{t('page.liveConsultation.generateFSPDialog.title')}</Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {!consultationId && !useLegacyMode ? (
          <Typography color="text.secondary">
            {t('page.liveConsultation.generateFSPDialog.noConsultation', {
              defaultValue: 'No consultation selected.',
            })}
          </Typography>
        ) : isLoadingActs ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Stack>
        ) : isError ? (
          <Typography color="error">
            {t('page.liveConsultation.generateFSPDialog.loadError', {
              defaultValue: 'Failed to load available acts.',
            })}
          </Typography>
        ) : availableActs.length === 0 ? (
          <Typography color="text.secondary">
            {t('page.liveConsultation.generateFSPDialog.noActs', {
              defaultValue: 'No acts available for this consultation.',
            })}
          </Typography>
        ) : (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('page.liveConsultation.generateFSPDialog.description')}
            </Typography>
            {availableActs.map((act) => (
              <Box
                key={act._id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1,
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <FormControlLabel
                  sx={{ flex: 1, mr: 0 }}
                  control={
                    <Checkbox
                      checked={checkedIds.has(act._id)}
                      onChange={() => toggleAct(act._id)}
                      size="small"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {act.libelle}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {act.id_transposition}
                      </Typography>
                    </Box>
                  }
                />
                <Typography variant="body2" sx={{ ml: 2, whiteSpace: 'nowrap', fontWeight: 500 }}>
                  {formatPrice(act.honoraire)}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          {t('common:button.cancel.label', { defaultValue: 'Cancel' })}
        </Button>
        <LoadingButton
          loading={isGenerating}
          onClick={handleConfirm}
          variant="contained"
          disabled={checkedIds.size === 0 || isLoadingActs}
        >
          {t('page.liveConsultation.documents.generateFSPButton.label')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default GenerateFspDialog
